/**
 * Sales-hierarchy node candidates for the industry `PropertyFilter`, read from
 * `ListHierarchyNodes`.
 *
 * This module used to ship the candidates as a compiled-in snapshot, because the names only
 * exist in DE and the API Lambda does not egress there by design. A sync-side job now
 * materialises them into Aurora and the service serves them from there, so the snapshot is
 * gone and the list is as fresh as that job's last run.
 *
 * Still a HINT, not an authority. The list can lag SFDC by up to one refresh cycle, and the
 * fetch can fail while the configuration itself loads fine. So typing is ALWAYS available, and
 * a value outside the list is reported as "not in the list" — never as "does not exist", which
 * would be a false claim.
 *
 * Grouped by level, not cascaded: the levels are not a strict tree (a single sh_l5 name sits
 * under several sh_l4s) and names repeat across levels, so candidates must stay separated by
 * level.
 *
 * The service returns only levels its `HierarchyLevel` enum names, so this module never has to
 * filter what it gets.
 */

import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import { getIvtClient, ListHierarchyNodesCommand } from '../../client/IvtHubClient';

/** What the page needs to render the picker and to hint about unknown values. */
export interface HierarchyNodeCatalog {
  /** `value` is the bare node name — that is what gets stored. */
  options: PropertyFilterProps.FilteringOption[];
  /** When the sync job last refreshed the list; absent until it first runs. */
  syncedAt?: Date;
  /**
   * Whether this level=name came back from the service.
   *
   * Absent from the list is NOT the same as absent from SFDC, so callers may hint but must not
   * block or claim the node does not exist.
   */
  isKnown: (level: string | undefined, name: string | undefined) => boolean;
}

/**
 * The catalog to use when the fetch failed.
 *
 * `isKnown` answers true for everything, so a failed fetch shows no candidates and no "not in
 * the list" hints — rather than flagging every already-configured node as unknown, which would
 * read as data loss.
 */
export const EMPTY_CATALOG: HierarchyNodeCatalog = {
  options: [],
  isKnown: () => true,
};

/**
 * Load the candidates.
 *
 * The caller should treat a rejection as non-fatal and fall back to {@link EMPTY_CATALOG}: the
 * page's job is editing the configuration, and the picker only makes that easier.
 */
export async function fetchHierarchyNodes(): Promise<HierarchyNodeCatalog> {
  const out = await getIvtClient().send(new ListHierarchyNodesCommand({}));

  const known = new Set<string>();
  const options: PropertyFilterProps.FilteringOption[] = [];
  for (const n of out.nodes ?? []) {
    if (!n.level || !n.name) continue;
    known.add(`${n.level}|${n.name}`);
    options.push({ propertyKey: n.level, value: n.name });
  }

  return {
    options,
    syncedAt: out.syncedAt,
    isKnown: (level, name) => !!level && !!name && known.has(`${level}|${name}`),
  };
}
